import { useEffect, useState, useRef } from "react";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Container } from "react-bootstrap";


import {
    selectCurrentUser,
    selectCurrentToken,
    logOut,
} from "../features/auth/authSlice";

import {
    fetchCart,
    addToCart,
    removeFromCart,
    clearState,
    addCart,
} from "../features/cartSlice";

import {
    fetchSearchProduct,
    clearSearchProduct,
    changeLoading,
} from "../features/productSlice";

import { useGetAllCategoriesQuery } from "../features/categoryApi";

// Components
import HeaderTop from "./headerTop";
import HeaderMiddle from "./HeaderMiddle";
import HeaderCategories from "./HeaderCategories";
import HeaderMobile from "./HeaderMobile";
import Login from "../common/login";
import Register from "../common/register";

// Utils
import logo from "../static/images/e-logo.png";

function Header() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const newRef = useRef(null);

    // Redux state
    const user = useSelector(selectCurrentUser);
    const token = useSelector(selectCurrentToken);
    const { cartListNew, cartAmount, cartCount, cartStatus, wishListCount } = useSelector((state) => state.cart);
    const { searchProductList } = useSelector((state) => state.product);
    const { data: categoryList } = useGetAllCategoriesQuery();

    // UI state
    const [scroll, setScroll] = useState(false);
    const [loginModalShow, setLoginModalShow] = useState(false);
    const [registerModalShow, setRegisterModalShow] = useState(false);
    const [mobileNo, setMobileNo] = useState("");
    const [otpScreen, setOtpScreen] = useState(false);
    const [gMapModalShow, setgMapModalShow] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState({});
    const [availBranches, setAvailBranches] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [offCanvasShow, setOffCanvasShow] = useState(false);
    const [searchSuggestion, setSearchSuggestion] = useState([]);
    const [showSearch, setShowSearch] = useState(false);
    const [searchKey, setSearchKey] = useState(null);

    const lastSelectedLocation = JSON.parse(localStorage.getItem("selectedLocation"));
    const storedBranches = JSON.parse(localStorage.getItem("availableBranches"));
    const lastSelectedBranch = JSON.parse(localStorage.getItem("selectedBranch"));
    // Location init
    useEffect(() => {
        window.addEventListener("scroll", () => setScroll(window.scrollY > 10));

        const hasBranches = storedBranches && storedBranches.length > 0;
        const hasSelectedBranch = !!lastSelectedBranch;

        // Location Check
        if (lastSelectedLocation) {
            setSelectedLocation({
                area: lastSelectedLocation.area,
                pincode: lastSelectedLocation.pincode,
            });
        } else {
            navigate("/");
            setgMapModalShow(true);
            return; // Exit early
        }

        // Branch Check

        if (hasBranches && hasSelectedBranch) {
            setSelectedBranch(lastSelectedBranch);
            setAvailBranches(storedBranches);
        } else if (!hasBranches && !hasSelectedBranch) {
            // Show GMap modal when both missing
            setgMapModalShow(true);
        } else if (lastSelectedLocation && !hasBranches && !hasSelectedBranch) {
            navigate("/location-unserviceable");
        }
    }, []);

    // Auto add cart refresh
    useEffect(() => {
        const warningTime = 1000;
        let warnTimeout;

        const warn = () => {
            if (cartStatus) dispatch(addCart());
        };

        const resetTimeout = () => {
            clearTimeout(warnTimeout);
            warnTimeout = setTimeout(warn, warningTime);
        };

        ["click"].forEach((event) =>
            window.addEventListener(event, resetTimeout)
        );

        resetTimeout();

        return () => {
            ["click"].forEach((event) =>
                window.removeEventListener(event, resetTimeout)
            );
            clearTimeout(warnTimeout);
        };
    }, [cartStatus]);

    useEffect(() => {
        dispatch(changeLoading());
        dispatch(fetchCart());
    }, [dispatch]);

    const logouts = () => {
        dispatch(logOut());
        dispatch(clearState());
    };

    useEffect(() => {
        setSelectedBranch(availBranches.length > 0 ? lastSelectedBranch : []);
    }, [availBranches]);

    useEffect(() => {
        if (searchProductList.length > 0) {
            const suggestions = searchProductList.map((pl) => {
                const price =
                    parseInt(pl.offer_type) === 3
                        ? parseInt(pl.value_type) === 0
                            ? parseFloat(pl.mrpPrice) * (1 - pl.offer_Value / 100)
                            : parseFloat(pl.mrpPrice) - pl.offer_Value
                        : parseFloat(pl.salePrice);

                const offerName =
                    parseInt(pl.offer_type) === 3
                        ? `Flat ${pl.offer_Value}${parseInt(pl.value_type) === 0 ? "% off" : " off"}`
                        : pl.offer_type === 4
                            ? `Buy ${pl.offer_qty} get ${pl.offer_free_quantity}`
                            : null;

                const itemInCart = cartListNew.find(
                    (item) => item.productItemId === pl.productItemId
                );

                return {
                    ...pl,
                    image: pl.image?.[0] || "",
                    salePrice: price,
                    offerName,
                    cartStatus: !!itemInCart,
                    cartQty: itemInCart?.quantity || 0,
                    unit: `${pl.unit} ${pl.unitName}`,
                };
            });

            // Compare old vs new suggestions to avoid infinite re-renders
            const suggestionsJSON = JSON.stringify(suggestions);
            const oldSuggestionsJSON = JSON.stringify(searchSuggestion);
            if (suggestionsJSON !== oldSuggestionsJSON) {
                setSearchSuggestion(suggestions);
            }

            if (!showSearch) setShowSearch(true);
        } else {
            if (showSearch) {
                setShowSearch(false);
            }
        }
    }, [searchProductList, cartListNew]);



    const debouncedSearch = useRef(_.debounce((value) => {
        if (value.length > 1) {
            dispatch(fetchSearchProduct(value));
        } else {
            dispatch(clearSearchProduct());
            setShowSearch(false);
        }
    }, 300)).current;

    const handleProductSearch = (e) => {
        const value = e.target.value;
        setSearchKey(value.length > 1 ? value : null);
        debouncedSearch(value);
    };

    const handleKeyPress = (event) => {
        if (event.key === "Enter" && searchKey) {
            navigate(`/search?q=${searchKey}`);
        }
    };

    const handleAddCart = (productItemId, qty, price) => {
        if (token) {
            dispatch(
                addToCart({
                    productItemId,
                    quantity: 1,
                    totalQuantity: 1,
                    salesPrice: price,
                })
            );
        } else {
            setLoginModalShow(true);
        }
    };

    const handleIncrement = (productItemId, qty, price) => {
        dispatch(
            addToCart({
                productItemId,
                quantity: 1,
                totalQuantity: 1,
                price,
                status: true,
            })
        );
    };

    const handleDecrement = (productItemId, qty, price) => {
        dispatch(removeFromCart({ productItemId, price }));
    };

    const truncateName = (name, maxLength) =>
        name && name.length > maxLength
            ? `${name.substring(0, maxLength)}...`
            : name;

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (newRef.current && !newRef.current.contains(e.target)) {
                if (showSearch) {
                    dispatch(clearSearchProduct());
                    setShowSearch(false);
                }
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () =>
            document.removeEventListener("mousedown", handleOutsideClick);
    });

    return (
        <Container fluid="xl" className="mb-3">
            <header className="d-none d-sm-block">
                <HeaderTop />
                <HeaderMiddle
                    scroll={scroll}
                    selectedLocation={selectedLocation}
                    truncateName={truncateName}
                    setgMapModalShow={setgMapModalShow}
                    gMapModalShow={gMapModalShow}
                    setSelectedLocation={setSelectedLocation}
                    availBranches={availBranches}
                    setAvailBranches={setAvailBranches}
                    handleProductSearch={handleProductSearch}
                    handleKeyPress={handleKeyPress}
                    showSearch={showSearch}
                    searchSuggestion={searchSuggestion}
                    handleAddCart={handleAddCart}
                    handleDecrement={handleDecrement}
                    handleIncrement={handleIncrement}
                    user={user}
                    token={token}
                    setLoginModalShow={setLoginModalShow}
                    cartAmount={cartAmount}
                    cartCount={cartCount}
                    logouts={logouts}
                    newRef={newRef}
                />
                <HeaderCategories
                    categoryList={categoryList}
                    offCanvasShow={offCanvasShow}
                    scroll={scroll}
                    user={user}
                    token={token}
                    wishListCount={wishListCount}
                    setLoginModalShow={setLoginModalShow}
                />
            </header>

            <HeaderMobile
                setgMapModalShow={setgMapModalShow}
                categoryList={categoryList}
                setOffCanvasShow={setOffCanvasShow}
                offCanvasShow={offCanvasShow}
                logo={logo}
                token={token}
                user={user}
                setLoginModalShow={setLoginModalShow}
                cartCount={cartCount}
                handleProductSearch={handleProductSearch}
                showSearch={showSearch}
                searchProductList={searchProductList}
            />

            <Login
                show={loginModalShow}
                tab="login-mobile"
                otpScreen={otpScreen}
                mobileNo={mobileNo}
                onHide={() => {
                    setLoginModalShow(false);
                    setOtpScreen(false);
                }}
                onRegister={() => {
                    setRegisterModalShow(true);
                    setLoginModalShow(false);
                    setOtpScreen(false);
                }}
            />

            <Register
                show={registerModalShow}
                onHide={() => setRegisterModalShow(false)}
                mobileNo={setMobileNo}
                otpShow={() => {
                    setRegisterModalShow(false);
                    setLoginModalShow(true);
                    setOtpScreen(true);
                }}
                onLogin={() => {
                    setLoginModalShow(true);
                    setRegisterModalShow(false);
                    setOtpScreen(false);
                }}
            />
        </Container>
    );
}

export default Header;
